import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatPayout, getGigCity, getGigPayout, parseList, resolveGigBackground } from "@/lib/utils";

export default function GigCard({
  gig,
  isInterested,
  isPending,
  canManageAvailability,
  isUsingFallback,
  isVenueUser,
  isOwnGig,
  onViewDetails,
  onToggleAvailability,
  onOpenInterestedModal,
  onEditGig,
  onDeleteGig,
}) {
  const city = getGigCity(gig);
  const payout = getGigPayout(gig);
  const genres = parseList(gig?.genres);
  const tags = parseList(gig?.tags);

  const canRespondToAvailability = canManageAvailability || isUsingFallback;
  const showInterestedButton = isVenueUser && !canRespondToAvailability;

  const availabilityButtonDisabled = showInterestedButton
    ? false
    : isPending || !canRespondToAvailability;

  const availabilityButtonLabel = showInterestedButton
    ? "Interested artists"
    : !canRespondToAvailability
      ? "Log in to respond"
      : isPending
        ? "Saving\u2026"
        : isInterested
          ? "Unmark Availability"
          : "I'm Available";

  const availabilityButtonClass = availabilityButtonDisabled
    ? "btn-secondary cursor-not-allowed opacity-60"
    : showInterestedButton
      ? "btn-secondary"
      : isInterested
        ? "btn-secondary"
        : "btn-primary";

  const cardBackgroundImage = resolveGigBackground(genres);
  const hasBackgroundImage = Boolean(cardBackgroundImage);
  const cardStyle = hasBackgroundImage
    ? {
        backgroundImage: cardBackgroundImage,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  const cardClassName = hasBackgroundImage
    ? "border border-transparent bg-transparent text-slate-100 shadow-lg"
    : "";
  const cardTitleClass = hasBackgroundImage
    ? "text-white"
    : "text-zinc-900 dark:text-zinc-50";
  const cardMetaClass = hasBackgroundImage
    ? "text-white/70"
    : "text-zinc-500 dark:text-zinc-400";
  const cardCityClass = hasBackgroundImage
    ? "border border-white/20 bg-white/10 text-white"
    : "bg-zinc-100 text-zinc-600 dark:bg-white/[0.08] dark:text-zinc-300";
  const cardPayoutClass = hasBackgroundImage
    ? "border border-white/20 bg-white/10 text-white"
    : "bg-zinc-900 text-white dark:bg-amber-500 dark:text-zinc-950";
  const cardGenreClass = hasBackgroundImage
    ? "border border-white/20 bg-white/10 text-white"
    : "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300";
  const cardTagClass = hasBackgroundImage
    ? "border-white/25 text-white/80"
    : "border-zinc-200 text-zinc-500 dark:border-white/[0.08] dark:text-zinc-400";
  return (
    <Card key={gig.id} className={cardClassName} style={cardStyle}>
      <CardHeader>
        <div className="min-w-0">
          <h2 className={`text-base font-semibold leading-snug ${cardTitleClass}`}>
            {gig.title}
          </h2>
          <div
            className={`mt-2 flex flex-wrap items-center gap-2 text-sm ${cardMetaClass}`}
          >
            <span>
              {gig.date ? new Date(gig.date).toLocaleDateString(undefined, { dateStyle: "medium" }) : "TBA"}
            </span>
            {city && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cardCityClass}`}
              >
                {city}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 text-right">
          {Number.isFinite(payout) && (
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${cardPayoutClass}`}
            >
              {formatPayout(payout)}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {genres.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {genres.map((genre) => (
              <span
                key={`${gig.id}-genre-${genre}`}
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${cardGenreClass}`}
              >
                {genre}
              </span>
            ))}
          </div>
        )}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 text-xs">
            {tags.map((tag) => (
              <span
                key={`${gig.id}-tag-${tag}`}
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide ${cardTagClass}`}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex items-center justify-between gap-2">
        <div className="flex gap-2 flex-wrap">
          <Button
            type="button"
            className="btn-secondary text-xs px-3 py-1.5"
            onClick={() => onViewDetails(gig.id)}
          >
            View details
          </Button>
          {(canRespondToAvailability || showInterestedButton) && (
            <Button
              type="button"
              className={`text-xs px-3 py-1.5 ${availabilityButtonClass}`}
              onClick={() => {
                if (showInterestedButton) { onOpenInterestedModal(gig); return; }
                onToggleAvailability(gig.id);
              }}
              disabled={isPending}
            >
              {availabilityButtonLabel}
            </Button>
          )}
          {isOwnGig && (
            <>
              <Button
                type="button"
                className="btn-secondary text-xs px-3 py-1.5"
                onClick={() => onEditGig(gig)}
              >
                Edit
              </Button>
              <Button
                type="button"
                className="text-xs px-3 py-1.5 btn-base border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800/50 dark:text-red-400 dark:hover:bg-red-900/20"
                onClick={() => onDeleteGig(gig)}
              >
                Delete
              </Button>
            </>
          )}
        </div>
        {isInterested && (
          <span className="text-[11px] font-medium text-emerald-500 dark:text-emerald-400 shrink-0">
            ✓ Available
          </span>
        )}
      </CardFooter>
    </Card>
  );
}
